import { Link } from "react-router-dom";
import { RefundPolicy } from "./RefundPolicy";
import { ArrowLeft } from "lucide-react";
import { ProductCard } from "./ProductCard";
import { SizeChart, POLO_ROWS, POLO_SIZES } from "./SizeChart";

const PRODUCTS = [
  {
    id: "classic",
    name: "The Classic Polo",
    description:
      "The everyday school polo. Breathable piqué cotton, a reinforced ribbed collar and a two-button placket — built for five days a week and the wash that follows.",
    image: "/products/polo-front.jpg",
    hoverImage: "/products/polo-back.jpg",
    hoverHint: "Hover to see back",
    shop: {
      id: "polo-classic",
      name: "The Classic Polo",
      price: 15,
      colours: ["White"],
      sizes: ["S", "M", "L"],
      logoOption: true,
    },
  },
  {
    id: "long-sleeve",
    name: "The Long-Sleeve Polo",
    description:
      "The same polo, made for the colder terms. Ribbed cuffs that hold their shape and the same collar — so the uniform looks the same all year.",
    image: "/products/polo-ls-front.jpg",
    hoverImage: "/products/polo-ls-back.jpg",
    hoverHint: "Hover to see back",
    shop: {
      id: "polo-longsleeve",
      name: "The Long-Sleeve Polo",
      price: 18,
      colours: ["White"],
      sizes: ["S", "M", "L"],
      logoOption: true,
    },
  },
];

const COLOURS = [{ name: "White", hex: "#F4F2EC", border: true }];

const eyebrow =
  "font-mono text-[11px] tracking-[0.25em] uppercase text-mutedink";

export const MaisonPique = () => (
  <div data-testid="maison-pique-page" className="min-h-screen flex flex-col">
    <header className="border-b border-ink/10 px-6 py-4 bg-[#F9F8F5]/90 backdrop-blur-md">
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        <Link
          to="/"
          data-testid="maison-pique-back-link"
          className="inline-flex items-center gap-2 text-sm text-mutedink hover:text-oxblood transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Portfolio
        </Link>
        <span className="font-cinzel text-xs sm:text-sm tracking-[0.25em] text-ink">
          HAILEY <span className="text-bronze">&amp;</span> CHLOE
        </span>
      </div>
    </header>

    <main className="flex-1 px-6 py-16 sm:py-20">
      <div className="max-w-5xl mx-auto">
        <p className={`${eyebrow} mb-4`}>
          A Hailey &amp; Chloe Holdings Project
        </p>
        <h1 className="font-serif font-light tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl">
          MAISON PIQUÉ
        </h1>
        <p
          data-testid="maison-pique-tagline"
          className="mt-3 font-serif italic text-xl text-oxblood"
        >
          School polos, done properly.
        </p>
        <p className="mt-6 max-w-xl text-base font-light text-[#2A2A2A] leading-relaxed">
          Crested piqué-cotton polos for the school day — reinforced collars,
          colours that hold, and a fit that lasts the whole year. Made for
          schools, ordered by families.
        </p>

        <div
          className="mt-14 grid sm:grid-cols-2 gap-6"
          data-testid="maison-pique-products"
        >
          {PRODUCTS.map((p) => (
            <ProductCard key={p.id} testId="pique" v={p} />
          ))}
        </div>

        <section
          data-testid="maison-pique-colours"
          className="mt-20 border-t border-ink/10 pt-14"
        >
          <p className={`${eyebrow} mb-3`}>Colours</p>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink mb-3">
            One colour. White.
          </h2>
          <p className="max-w-xl text-sm text-mutedink leading-relaxed mb-10">
            Every polo comes in classic white — the colour that works with any
            school uniform and washes clean, week after week.
          </p>
          <ul className="grid grid-cols-3 sm:grid-cols-6 gap-6">
            {COLOURS.map((c) => (
              <li
                key={c.name}
                data-testid={`pique-colour-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-center"
              >
                <span
                  aria-hidden="true"
                  style={{ backgroundColor: c.hex }}
                  className={`block aspect-square w-full ${c.border ? "border border-ink/15" : ""}`}
                />
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
                  {c.name}
                </p>
              </li>
            ))}
          </ul>
        </section>

        <SizeChart
          product="Classic Polo"
          rows={POLO_ROWS}
          sizes={POLO_SIZES}
          footnote="S fits roughly 140 cm tall · M 150 cm · L 160 cm. Tolerance ±1.5 cm on length and chest · ±1 cm on shoulder"
        />

        <RefundPolicy brand="Maison Piqué" />
      </div>
    </main>

    <footer className="bg-[#111111] text-[#F9F8F5] px-6 py-8">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="font-cinzel text-sm tracking-[0.2em]">
          MAISON <span className="text-bronze">PIQUÉ</span>
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F9F8F5]/40">
          © Hailey and Chloe Holdings Inc.
        </p>
      </div>
    </footer>
  </div>
);
